import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ChevronLeft, Loader2, RefreshCcw, Sun, Moon, Smartphone, Package, ClipboardList, CreditCard, UserCheck, Home, ShoppingBag, Wallet, LogOut, Menu, Search, ChevronDown, AlertTriangle, Copy, ClipboardCheck, Send, X } from "lucide-react";
import { addDoc, collection, doc, getDoc, runTransaction, serverTimestamp, getDocs, query, where, orderBy, onSnapshot, updateDoc, setDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db, mirrorDb, isMirrorTransactionsEnabled } from "../firebase";
import { assertUkConnectDataNumber } from "../utils/ukConnectDataNumbers";

const PAYSTACK_PUBLIC_KEY =
  import.meta.env?.VITE_PAYSTACK_KEY ||
  process.env.REACT_APP_PAYSTACK_KEY ||
  "pk_live_8545c70ef4fb392f45389ba119cb921c7911141d";

const MNOTIFY_KEY =
  import.meta.env?.VITE_MNOTIFY_KEY ||
  process.env.REACT_APP_MNOTIFY_KEY ||
  "zov6giqNXoQeyMdHQeqgXkOCa";

const SMS_SENDER_ID =
  import.meta.env?.VITE_SMS_SENDER_ID ||
  process.env.REACT_APP_SMS_SENDER_ID ||
  "KyekyekuTec";

const ADMIN_SMS_PHONE = "0545454000";
const DEFAULT_MANUAL_PAYMENT_NUMBER = "0545454000";
const DEFAULT_MANUAL_PAYMENT_NAME = "KYETECH ADMIN";

const rawDataFieApiKey =
  import.meta.env?.VITE_DATAFIE_API_KEY ||
  process.env.REACT_APP_DATAFIE_API_KEY ||
  "";
const DATAFIE_API_KEY = String(rawDataFieApiKey || "").trim();

const DATAFIE_WEBHOOK_URL =
  import.meta.env?.VITE_DATAFIE_WEBHOOK_URL ||
  process.env.REACT_APP_DATAFIE_WEBHOOK_URL ||
  "https://ukconnectivity.site/api/datafie-webhook.php";

const sanitizeBaseUrl = (baseUrl) => {
  const defaultUrl = "https://data-fie.com/api";
  const raw = String(baseUrl || "").trim();
  if (!raw) return defaultUrl;
  try {
    const url = new URL(raw);
    if (!url.protocol.startsWith("http") || /_/.test(url.hostname)) {
      throw new Error("Invalid Data Fie base URL");
    }
    return `${url.protocol}//${url.hostname}${url.pathname.replace(/\/+$/, "")}`;
  } catch (err) {
    console.warn("Invalid Data Fie base URL, falling back to default:", raw);
    return defaultUrl;
  }
};

const DATAFIE_BASE_URL = sanitizeBaseUrl(
  import.meta.env?.VITE_DATAFIE_BASE_URL || process.env.REACT_APP_DATAFIE_BASE_URL
);

const NETWORK_OPTIONS = ["MTN", "AirtelTigo", "Telecel"];

const generateFallbackPackages = (network) => {
  // Simple fallback pricing per GB by network
  const basePerGb = network === "MTN" ? 15 : network === "AirtelTigo" ? 14 : 12;
  const pkgs = [];
  for (let i = 1; i <= 100; i++) {
    const id = `${network} ${i}GB`;
    const price = Number((i * basePerGb).toFixed(2));
    pkgs.push({ id, label: `${i}GB - GHS ${price.toFixed(2)}`, price });
  }
  return pkgs;
};

const DATA_PACKAGE_OPTIONS = {
  MTN: generateFallbackPackages("MTN"),
  AirtelTigo: generateFallbackPackages("AirtelTigo"),
  Telecel: generateFallbackPackages("Telecel"),
};

const actionTiles = [
  { title: "Airtime", icon: Smartphone },
  { title: "Data Bundle", icon: Package },
  { title: "Instant Bundles", icon: CreditCard },
  { title: "Wallet", icon: CreditCard },
  { title: "Purchases", icon: ClipboardList },
  { title: "Afa Registration", icon: UserCheck },
];

const menuItems = [
  { title: "Home", icon: Home, value: null },
  { title: "Purchases", icon: ShoppingBag, value: "Purchases" },
  { title: "Wallet", icon: Wallet, value: "Wallet" },
];

const StatusBadge = ({ status }) => {
  const lower = String(status || "pending").toLowerCase();
  if (lower === "pending") {
    return (
      <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold">
        <Loader2 size={18} className="animate-spin" />
        Pending
      </div>
    );
  }

  if (lower === "processing") {
    return (
      <div className="flex items-center justify-center gap-2 text-blue-400 font-bold">
        <RefreshCcw size={18} className="animate-spin" />
        Processing
      </div>
    );
  }

  if (lower === "completed") {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 220 }}
        className="flex items-center justify-center gap-2 text-green-400 font-bold"
      >
        <CheckCircle size={18} />
        Completed
      </motion.div>
    );
  }

  return <span className="text-gray-400 font-bold">{String(status || "Unknown").toUpperCase()}</span>;
};

const NetworkStatusBadge = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  const map = {
    good: { color: "bg-green-500", anim: "" },
    slow: { color: "bg-yellow-400", anim: "" },
    bad: { color: "bg-red-500", anim: "animate-ping" },
    down: { color: "bg-red-500", anim: "animate-ping" },
  };
  const current = map[normalized] || { color: "bg-gray-400", anim: "" };
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${current.color} ${current.anim}`} />;
};

export default function AgentDashboard() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [profileName, setProfileName] = useState("Agent");
  const [walletBalance, setWalletBalance] = useState("0.00");
  const [walletGrowth, setWalletGrowth] = useState("Live wallet snapshot");
  const [activeSales, setActiveSales] = useState("2 active orders");

  const [selectedTile, setSelectedTile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("cheapzone-theme") || "dark");
  const [showWalletView, setShowWalletView] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [purchaseSearchPhone, setPurchaseSearchPhone] = useState("");
  const [purchaseSearchResults, setPurchaseSearchResults] = useState([]);
  const [loadingPurchaseSearch, setLoadingPurchaseSearch] = useState(false);
  const [purchaseSearchError, setPurchaseSearchError] = useState("");
  const [dataNetwork, setDataNetwork] = useState("MTN");
  const [selectedDataPackageId, setSelectedDataPackageId] = useState("");
  const [beneficiaryPhone, setBeneficiaryPhone] = useState("");
  const [dataBundleStatus, setDataBundleStatus] = useState("");
  const [dataBundleError, setDataBundleError] = useState("");
  const [loadingDataBundle, setLoadingDataBundle] = useState(false);
  const [airtimeNetwork, setAirtimeNetwork] = useState("MTN");
  const [airtimeNumber, setAirtimeNumber] = useState("");
  const [airtimeAmount, setAirtimeAmount] = useState("");
  const [airtimeStatus, setAirtimeStatus] = useState("");
  const [airtimeError, setAirtimeError] = useState("");
  const [loadingAirtime, setLoadingAirtime] = useState(false);

  // Admin-loaded packages (for data bundles) — hooks must be at top level
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({});

  // Top-up flow state (Paystack & Manual)
  const [topupAmount, setTopupAmount] = useState(0);
  const [topupMethod, setTopupMethod] = useState("paystack");
  const [topupManualReason, setTopupManualReason] = useState("");
  const [loadingTopup, setLoadingTopup] = useState(false);
  const [manualReference, setManualReference] = useState("");
  const [manualPaymentNumber, setManualPaymentNumber] = useState(DEFAULT_MANUAL_PAYMENT_NUMBER);
  const [manualPaymentName, setManualPaymentName] = useState(DEFAULT_MANUAL_PAYMENT_NAME);
  const [copiedField, setCopiedField] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Helper functions for top-up flow
  const showError = (msg) => setErrorMsg(msg);
  const showSuccess = (msg) => setSuccessMsg(msg);

  function generateManualReference() {
    const letter = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    const numbers = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const ref = `${letter}${numbers}`;
    setManualReference(ref);
    return ref;
  }

  function normalizePhone(phoneNumber) {
    const raw = String(phoneNumber || "").replace(/\s+/g, "");
    if (raw.startsWith("0") && raw.length === 10) {
      return `233${raw.slice(1)}`;
    }
    return raw;
  }

  async function sendAdminSms(message) {
    const recipient = normalizePhone(ADMIN_SMS_PHONE);
    if (!recipient) {
      throw new Error("Invalid admin phone number for SMS alert.");
    }

    const response = await fetch(`https://api.mnotify.com/api/sms/quick?key=${encodeURIComponent(MNOTIFY_KEY)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        recipient: [recipient],
        sender: SMS_SENDER_ID,
        message: String(message || "").trim(),
      }),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok || (data && !data.success && String(data.status || "").toLowerCase() !== "success")) {
      console.error("Admin SMS failure:", response.status, data);
      throw new Error(data?.message || "Unable to send SMS alert to admin.");
    }
  }

  async function handleTopupWithPaystack() {
    showError("");
    showSuccess("");

    if (topupAmount <= 0) {
      showError("Enter a valid top-up amount.");
      return;
    }

    if (!user?.email) {
      showError("Email not found. Unable to proceed.");
      return;
    }

    setLoadingTopup(true);

    try {
      if (!window?.PaystackPop?.setup) {
        throw new Error("Payment service is not available right now. Please refresh and try again.");
      }

      const onPaystackSuccess = (res) => {
        const reference = res?.reference || res?.trxref || res?.trans || "";

        if (!reference) {
          setLoadingTopup(false);
          showError("Payment callback received without reference. Please contact admin.");
          return;
        }

        void handleTopupSuccess(reference);
      };

      const paystackFeePercent = 0.02;
      const amountInCents = Math.round(Number(topupAmount) * 100);
      const feeInCents = Math.round(amountInCents * paystackFeePercent);
      const totalAmountInCents = amountInCents + feeInCents;

      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: totalAmountInCents,
        currency: "GHS",
        ref: `TOPUP-${user.uid}-${Date.now()}`,
        onClose: () => {
          setLoadingTopup(false);
          showError("Top-up payment was cancelled.");
        },
        callback: onPaystackSuccess,
      });

      if (!handler?.openIframe) {
        throw new Error("Unable to open payment window. Please try again.");
      }

      handler.openIframe();
    } catch (paystackError) {
      const rawMessage = String(paystackError?.message || "");
      const isQuotaExceeded = /quota\s*exceeded/i.test(rawMessage);

      setLoadingTopup(false);

      if (isQuotaExceeded) {
        setTopupMethod("manual");
        showError("Paystack quota exceeded. Please use Manual for now or update your Paystack public key.");
        return;
      }

      showError(rawMessage || "Unable to start payment. Please try again.");
    }
  }

  async function handleTopupSuccess(ref) {
    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const currentBalance = Number(userSnap.data()?.walletBalance || 0);
      const newBalance = currentBalance + topupAmount;
      const paystackFeeAmount = Number((topupAmount * 0.02).toFixed(2));

      await updateDoc(userRef, {
        walletBalance: newBalance,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "walletTransactions"), {
        agentId: user.uid,
        amount: topupAmount,
        paystackFee: paystackFeeAmount,
        totalCharged: topupAmount + paystackFeeAmount,
        balanceBefore: currentBalance,
        balanceAfter: newBalance,
        type: "topup",
        method: "paystack",
        paystackRef: ref,
        createdAt: serverTimestamp(),
      });

      setWalletBalance(newBalance.toFixed(2));
      showSuccess(`Top-up successful! GHS ${topupAmount.toFixed(2)} added to wallet.`);
      setTopupAmount(0);
      setShowDepositModal(false);
      setLoadingTopup(false);
    } catch (err) {
      const errCode = String(err?.code || "").toLowerCase();
      const errMessage = String(err?.message || "");
      const quotaExceeded = errCode.includes("resource-exhausted") || /quota\s*exceeded/i.test(errMessage);

      if (quotaExceeded) {
        showError("Payment may have completed, but Firestore quota exceeded. Contact admin with your Paystack reference.");
      } else {
        showError("Top-up failed. Please contact admin.");
      }
      setLoadingTopup(false);
    }
  }

  async function handleManualPaymentConfirmation() {
    showError("");
    showSuccess("");

    if (topupAmount <= 0) {
      showError("Enter a valid top-up amount before confirming payment.");
      return;
    }

    if (!String(manualReference || "").trim()) {
      showError("Generate a payment reference before tapping I Have Paid.");
      return;
    }

    setLoadingTopup(true);

    try {
      const paymentReference = manualReference.trim();
      const agentName = profileName || user?.email || "Agent";

      await addDoc(collection(db, "topupRequests"), {
        agentId: user.uid,
        agentEmail: user?.email || "",
        agentName,
        amount: topupAmount,
        reason: topupManualReason.trim(),
        status: "paid_pending_confirmation",
        paymentChannel: "momo_direct_deposit",
        paymentReference,
        momoNumber: manualPaymentNumber,
        momoName: manualPaymentName,
        paidAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });

      const smsMessage = [
        "MANUAL TOPUP ALERT",
        `AGENT NAME: ${agentName}`,
        `AMOUNT: GHS ${Number(topupAmount).toFixed(2)}`,
        `REFERENCE: ${paymentReference}`,
      ].join("\n");

      await sendAdminSms(smsMessage);

      showSuccess("Payment confirmation sent to admin by SMS. Wallet will be credited after verification.");
      setTopupAmount(0);
      setTopupManualReason("");
      setManualReference("");
      setShowDepositModal(false);
      setLoadingTopup(false);
    } catch (confirmError) {
      showError(confirmError.message || "Unable to confirm manual payment right now.");
      setLoadingTopup(false);
    }
  }

  const isDark = theme === "dark";
  const orderTransactions = transactions.filter((t) => t._source === "credentials");

  // Debug: Log order transactions to verify data
  useEffect(() => {
    if (transactions.length > 0) {
      console.log("Transactions loaded:", transactions.length, transactions);
      console.log("Order transactions:", orderTransactions.length, orderTransactions);
    }
  }, [transactions, orderTransactions]);

  useEffect(() => {
    localStorage.setItem("cheapzone-theme", theme);
    document.documentElement.classList.toggle("dark", isDark);
  }, [theme, isDark]);

  const getPackageCollection = (network) => {
    switch (network) {
      case "MTN":
        return "packages_MTN";
      case "AirtelTigo":
        return "packages_AirtelTigo";
      case "Telecel":
        return "packages_Telecel";
      default:
        return "packages_MTN";
    }
  };

  useEffect(() => {
    let active = true;
    async function loadPackages() {
      setLoadingPackages(true);
      try {
        const colName = getPackageCollection(dataNetwork);
        const snap = await getDocs(collection(db, colName));
        if (!active) return;
        setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (!active) return;
        setPackages([]);
      } finally {
        if (!active) return;
        setLoadingPackages(false);
      }
    }
    loadPackages();
    return () => {
      active = false;
    };
  }, [dataNetwork]);

  // realtime network status listener (reflect admin changes immediately)
  useEffect(() => {
    try {
      const unsub = onSnapshot(doc(db, "network_status", "current"), (snap) => {
        if (snap.exists()) setNetworkStatus(snap.data());
        else setNetworkStatus({});
      }, (err) => console.error(err));
      return () => unsub();
    } catch (e) {
      console.error(e);
    }
  }, []);

  const packagesForNetwork = (() => {
    if (packages && packages.length) {
      return packages
        .filter((p) => !p.network || p.network === dataNetwork)
        .map((p) => {
          const base = Number(p.agentPrice ?? p.userPrice ?? p.price ?? 0);
          const match = String(p.id || "").match(/(\d+)/);
          const gb = match ? Number(match[1]) : base || 0;
          return { id: p.id, label: `${p.id} - GHS ${base.toFixed(2)}`, price: base, gb };
        })
        .sort((a, b) => (a.gb || 0) - (b.gb || 0));
    }
    // fallback options are already in ascending order, but ensure numeric sort
    return (DATA_PACKAGE_OPTIONS[dataNetwork] || []).map((p) => {
      const match = String(p.id || "").match(/(\d+)/);
      const gb = match ? Number(match[1]) : 0;
      return { ...p, gb };
    }).sort((a, b) => (a.gb || 0) - (b.gb || 0));
  })();

  useEffect(() => {
    let active = true;

    async function loadProfile() {
      if (!user) {
        return;
      }

      const snap = await getDoc(doc(db, "users", user.uid));
      if (!active || !snap.exists()) {
        return;
      }

      const data = snap.data();
      const fullName = data?.fullName || `${data?.firstName || ""} ${data?.lastName || ""}`.trim() || "Agent";
      setProfileName(fullName);
      setWalletBalance(Number(data?.walletBalance || 0).toFixed(2));
      setWalletGrowth(data?.walletGrowthText || "Live wallet snapshot");
      setActiveSales(data?.activeSalesText || "2 active orders");
    }

    loadProfile();
    return () => {
      active = false;
    };
  }, [user]);

  async function handleLogout() {
    await signOut(auth);
    navigate("/agent/login", { replace: true });
  }

  const getDataFieNetworkCode = (network) => {
    switch (network) {
      case "MTN":
        return "mtn";
      case "Telecel":
        return "telecel";
      case "AirtelTigo":
        return "airtel";
      default:
        return null;
    }
  };

  const getDataFieBundleId = (pkg) => {
    if (!pkg) return null;
    if (Number.isInteger(pkg.bundleId)) return pkg.bundleId;
    const idValue = String(pkg.id || "").trim();
    if (/^\d+$/.test(idValue)) return Number(idValue);
    const match = idValue.match(/(\d+)/);
    return match ? Number(match[1]) : null;
  };

  const postToDataFie = async (payload) => {
    if (!DATAFIE_API_KEY) {
      throw new Error("Data Fie API key is not configured. Set REACT_APP_DATAFIE_API_KEY in .env and restart the app.");
    }

    const defaultDataFieBaseUrl = "https://data-fie.com/api";
    const normalizeBaseUrl = (baseUrl) => String(baseUrl || defaultDataFieBaseUrl).replace(/\/+$/, "");
    const buildEndpoint = (baseUrl) => `${normalizeBaseUrl(baseUrl)}/process-order.php`;
    const endpoint = buildEndpoint(DATAFIE_BASE_URL);
    const fallbackEndpoint = buildEndpoint(defaultDataFieBaseUrl);
    const requestBody = {
      ...payload,
      key: DATAFIE_API_KEY,
      api_key: DATAFIE_API_KEY,
    };

    console.debug("Data Fie request", {
      endpoint,
      fallbackEndpoint,
      apiKeyLength: DATAFIE_API_KEY.length,
      payload,
      requestBodyKeys: Object.keys(requestBody),
      useAuthHeader: true,
    });

    const makeRequest = async (url, includeAuthHeader) => {
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      if (includeAuthHeader) {
        headers.Authorization = `Bearer ${DATAFIE_API_KEY}`;
      }

      const response = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers,
        body: JSON.stringify(requestBody),
      });

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (err) {
        throw new Error(`Data Fie response parse failed: ${text}`);
      }

      console.debug("Data Fie response", { url, status: response.status, text, data });

      if (response.ok && (data?.success || String(data?.status || "").toLowerCase().includes("success"))) {
        return data;
      }

      const message = data?.message || data?.error || text || `HTTP ${response.status}`;
      throw new Error(message);
    };

    const isNetworkError = (message) => /failed to fetch|networkerror|getaddrinfo|enotfound|eai_again|econnrefused|econnreset/i.test(message);

    try {
      return await makeRequest(endpoint, true);
    } catch (err) {
      const lower = String(err.message || "").toLowerCase();
      if (isNetworkError(lower) && endpoint !== fallbackEndpoint) {
        try {
          return await makeRequest(fallbackEndpoint, true);
        } catch (fallbackErr) {
          const lowerFallback = String(fallbackErr.message || "").toLowerCase();
          if (!isNetworkError(lowerFallback)) {
            throw fallbackErr;
          }
        }
      }

      if (lower.includes("invalid api key") || lower.includes("unauthorized") || lower.includes("invalid key")) {
        const guidance =
          "Data Fie auth failed. This endpoint uses JSON request payloads and requires the API key. Confirm REACT_APP_DATAFIE_API_KEY and REACT_APP_DATAFIE_BASE_URL in .env, then restart the app.";
        const authError = new Error(`${guidance} (${err.message})`);
        authError.name = "DataFieAuthError";
        throw authError;
      }

      throw err;
    }
  };

  async function handleDataBundlePurchase() {
    setDataBundleStatus("");
    setDataBundleError("");

    const cleanNumber = String(beneficiaryPhone || "").trim();
    const selectedPackage = selectedDataPackageId;
    const packageDetails = packagesForNetwork.find((pkg) => pkg.id === selectedPackage);
    const orderAmount = Number(packageDetails?.price ?? 0);
    const dataFieNetwork = getDataFieNetworkCode(dataNetwork);
    const dataFieBundleId = getDataFieBundleId(packageDetails);

    const selectedNetStatus = String(networkStatus?.[dataNetwork]?.status || "good").toLowerCase();
    if (["bad", "down"].includes(selectedNetStatus)) {
      setDataBundleError(`${dataNetwork} network is currently bad.`);
      return;
    }

    if (!selectedPackage) {
      setDataBundleError("Choose a package.");
      return;
    }

    if (!cleanNumber) {
      setDataBundleError("Enter beneficiary phone number.");
      return;
    }

    try {
      await assertUkConnectDataNumber(cleanNumber);
    } catch (error) {
      setDataBundleError(error?.message || "This number cannot buy data.");
      return;
    }

    if (!dataFieNetwork) {
      setDataBundleError("Selected network is not supported by Data Fie.");
      return;
    }

    if (!dataFieBundleId) {
      setDataBundleError("Unable to determine bundle id for the selected package.");
      return;
    }

    if (!Number.isFinite(orderAmount) || orderAmount <= 0) {
      setDataBundleError("Selected package price is invalid. Please choose another package.");
      return;
    }

    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      setDataBundleError("Agent profile not found.");
      return;
    }

    const walletBalanceValue = Number(userSnap.data()?.walletBalance || 0);
    if (!Number.isFinite(walletBalanceValue)) {
      setDataBundleError("Wallet balance is invalid. Please contact admin.");
      return;
    }

    if (walletBalanceValue < orderAmount) {
      setDataBundleError("Insufficient wallet balance.");
      return;
    }

    setLoadingDataBundle(true);

    let orderData = {};
    let requestReference = `DF-${Date.now()}`;
    let apiError = null;
    let orderStatus = "pending";
    let dataFieStatus = "failed_to_send";
    let savedManually = false;

    try {
      const result = await postToDataFie({
        network: dataFieNetwork,
        bundle_id: dataFieBundleId,
        phone: cleanNumber,
      });
      orderData = result.data || {};
      requestReference = orderData.reference || orderData.order_id || requestReference;
      orderStatus = orderData.status || "pending";
      dataFieStatus = orderData.status || "pending";
    } catch (err) {
      apiError = err;
      savedManually = true;
      orderData = {};
      requestReference = `MANUAL-${Date.now()}`;
      orderStatus = "pending";
      dataFieStatus = "failed_to_send";
    }

    let createdOrderId = null;
    let createdBalanceBefore = 0;
    let createdBalanceAfter = 0;

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent profile not found.");
        }

        const walletBalanceValue = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(walletBalanceValue)) {
          throw new Error("Wallet balance is invalid. Please contact admin.");
        }

        if (walletBalanceValue < orderAmount) {
          throw new Error("Insufficient wallet balance.");
        }

        const nextBalance = Number((walletBalanceValue - orderAmount).toFixed(2));
        const orderRef = doc(collection(db, "credentials"));
        createdOrderId = orderRef.id;
        createdBalanceBefore = walletBalanceValue;
        createdBalanceAfter = nextBalance;
        const walletRef = doc(collection(db, "walletTransactions"));

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(orderRef, {
          assignedTo: cleanNumber,
          network: dataNetwork,
          packageId: selectedPackage,
          product: selectedPackage,
          totalPaid: orderAmount,
          status: orderStatus,
          type: "data",
          assignedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          source: "agent",
          agentId: user.uid,
          agentEmail: user.email,
          agentName: profileName,
          balanceBefore: walletBalanceValue,
          balanceAfter: nextBalance,
          note: savedManually
            ? `Saved for manual processing after Data Fie failure: ${String(apiError?.message || "Unknown error")}`
            : `Data Fie order ${requestReference} for ${cleanNumber}`,
          dataFieOrderId: orderData.order_id || null,
          dataFieReference: orderData.reference || null,
          dataFieStatus,
          dataFieResponse: orderData || { error: String(apiError?.message || "Unknown error") },
          webhookUrl: DATAFIE_WEBHOOK_URL,
          requiresManualProcessing: savedManually,
        });

        transaction.set(walletRef, {
          agentId: user.uid,
          agentEmail: user.email,
          agentName: profileName,
          amount: -orderAmount,
          balanceBefore: walletBalanceValue,
          balanceAfter: nextBalance,
          type: "purchase",
          method: "wallet",
          status: "completed",
          packageId: selectedPackage,
          beneficiary: cleanNumber,
          network: dataNetwork,
          note: savedManually
            ? `Manual order saved after Data Fie failure: ${String(apiError?.message || "Unknown error")}`
            : `Data Fie order ${requestReference}`,
          dataFieReference: orderData.reference || null,
          createdAt: serverTimestamp(),
        });
      });

      if (createdOrderId && (await isMirrorTransactionsEnabled())) {
        try {
          await setDoc(doc(mirrorDb, "credentials", createdOrderId), {
            sourceDocId: createdOrderId,
            assignedTo: cleanNumber,
            network: dataNetwork,
            packageId: selectedPackage,
            product: selectedPackage,
            totalPaid: orderAmount,
            status: orderStatus,
            type: "data",
            source: "agent",
            agentId: user.uid,
            agentEmail: user.email,
            agentName: profileName,
            balanceBefore: createdBalanceBefore,
            balanceAfter: createdBalanceAfter,
            note: savedManually
              ? `Manual order saved after Data Fie failure: ${String(apiError?.message || "Unknown error")}`
              : `Data Fie order ${requestReference} for ${cleanNumber}`,
            dataFieOrderId: orderData.order_id || null,
            dataFieReference: orderData.reference || null,
            dataFieStatus,
            assignedAt: serverTimestamp(),
          });
        } catch {
          // Keep purchase successful even if mirror write fails.
        }
      }

      if (savedManually) {
        setDataBundleStatus(
          `Order has been saved Successfully.`
        );
      } else {
        setDataBundleStatus(`Order sent to Data Fie successfully. Reference: ${requestReference}`);
      }
      setSelectedDataPackageId("");
      setBeneficiaryPhone("");

      // Refresh transactions immediately after successful purchase or fallback save
      await refreshTransactions();
      setDataNetwork("MTN");
    } catch (error) {
      const errorMessage = String(error?.message || "");
      if (/failed to fetch|getaddrinfo|enotfound|networkerror|eai_again/i.test(errorMessage)) {
        setDataBundleError(
          "Unable to reach Data Fie server. Check your network and Data Fie base URL configuration."
        );
      } else {
        setDataBundleError(errorMessage || "Unable to submit data bundle request. Please try again.");
      }
    } finally {
      setLoadingDataBundle(false);
    }
  }

  async function handleAirtimePurchase() {
    setAirtimeStatus("");
    setAirtimeError("");

    const cleanNumber = String(airtimeNumber || "").trim();
    const purchaseAmount = Number(airtimeAmount || 0);

    if (!cleanNumber) {
      setAirtimeError("Enter phone number.");
      return;
    }

    if (purchaseAmount <= 0 || !Number.isFinite(purchaseAmount)) {
      setAirtimeError("Enter a valid amount.");
      return;
    }

    if (!airtimeNetwork) {
      setAirtimeError("Choose a network.");
      return;
    }

    const airtimeNetStatus = String(networkStatus?.[airtimeNetwork]?.status || "good").toLowerCase();
    if (["bad", "down"].includes(airtimeNetStatus)) {
      setAirtimeError(`${airtimeNetwork} network is currently bad.`);
      return;
    }

    setLoadingAirtime(true);

    try {
      let createdOrderId = null;
      let createdBalanceBefore = 0;
      let createdBalanceAfter = 0;

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error("Agent profile not found.");
        }

        const walletBalanceValue = Number(userSnap.data().walletBalance || 0);
        if (!Number.isFinite(walletBalanceValue)) {
          throw new Error("Wallet balance is invalid. Please contact admin.");
        }

        if (walletBalanceValue < purchaseAmount) {
          throw new Error("Insufficient wallet balance.");
        }

        const nextBalance = Number((walletBalanceValue - purchaseAmount).toFixed(2));
        const orderRef = doc(collection(db, "credentials"));
        createdOrderId = orderRef.id;
        createdBalanceBefore = walletBalanceValue;
        createdBalanceAfter = nextBalance;
        const walletRef = doc(collection(db, "walletTransactions"));

        transaction.update(userRef, {
          walletBalance: nextBalance,
          updatedAt: serverTimestamp(),
        });

        transaction.set(orderRef, {
          requestNumber: cleanNumber,
          network: airtimeNetwork,
          amount: purchaseAmount,
          status: "pending",
          type: "airtime",
          assignedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          source: "agent",
          agentId: user.uid,
          agentEmail: user.email,
          agentName: profileName,
          balanceBefore: walletBalanceValue,
          balanceAfter: nextBalance,
          note: `Airtime order for ${cleanNumber}`,
        });

        transaction.set(walletRef, {
          agentId: user.uid,
          agentEmail: user.email,
          agentName: profileName,
          amount: -purchaseAmount,
          balanceBefore: walletBalanceValue,
          balanceAfter: nextBalance,
          type: "purchase",
          method: "wallet",
          status: "completed",
          requestNumber: cleanNumber,
          network: airtimeNetwork,
          note: "Agent wallet airtime purchase",
          createdAt: serverTimestamp(),
        });
      });

      if (createdOrderId && (await isMirrorTransactionsEnabled())) {
        try {
          await setDoc(doc(mirrorDb, "credentials", createdOrderId), {
            sourceDocId: createdOrderId,
            requestNumber: cleanNumber,
            network: airtimeNetwork,
            amount: purchaseAmount,
            status: "pending",
            type: "airtime",
            source: "agent",
            agentId: user.uid,
            agentEmail: user.email,
            agentName: profileName,
            balanceBefore: createdBalanceBefore,
            balanceAfter: createdBalanceAfter,
            note: `Airtime order for ${cleanNumber}`,
            assignedAt: serverTimestamp(),
          });
        } catch {
          // Keep purchase successful even if mirror write fails.
        }
      }

      setAirtimeStatus("Airtime request sent. Admin will process it shortly.");
      setAirtimeNumber("");
      setAirtimeAmount("");
      setAirtimeNetwork("MTN");
      
      // Refresh transactions immediately after successful purchase
      await refreshTransactions();
    } catch (error) {
      setAirtimeError(
        error?.message || "Unable to submit airtime request. Please try again."
      );
    } finally {
      setLoadingAirtime(false);
    }
  }

  async function handleLoadPurchases() {
    setPurchaseSearchError("");
    setPurchaseSearchResults([]);
    if (!user) return;

    setLoadingPurchaseSearch(true);
    try {
      const cleanSearch = String(purchaseSearchPhone || "").trim();
      let snapshots = [];

      if (cleanSearch) {
        snapshots = await Promise.all([
          getDocs(query(collection(db, "credentials"), where("requestNumber", "==", cleanSearch))),
          getDocs(query(collection(db, "credentials"), where("assignedTo", "==", cleanSearch))),
          getDocs(query(collection(db, "credentials"), where("beneficiary", "==", cleanSearch))),
        ]);
      } else {
        snapshots = [await getDocs(query(collection(db, "credentials"), where("agentId", "==", user.uid)))]
      }

      const rows = [];
      snapshots.forEach((snap) => {
        snap.docs.forEach((docItem) => {
          const data = docItem.data();
          if (data.source === "agent" || data.agentId === user.uid) {
            rows.push({ id: docItem.id, ...data });
          }
        });
      });

      const uniqueRows = Array.from(new Map(rows.map((row) => [row.id, row])).values());
      uniqueRows.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt || 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt || 0;
        return bTime - aTime;
      });

      setPurchaseSearchResults(uniqueRows);
    } catch (err) {
      setPurchaseSearchError(err?.message || "Unable to load purchases.");
    } finally {
      setLoadingPurchaseSearch(false);
    }
  }

  // Transactions loader (wallet activity + credentials/orders by this agent)
  useEffect(() => {
    if (!user) return;
    setLoadingTransactions(true);
    try {
      const txQ = query(collection(db, "transactions"), where("userId", "==", user.uid), orderBy("createdAt", "desc"));
      const credQ = query(collection(db, "credentials"), where("agentId", "==", user.uid), orderBy("createdAt", "desc"));
      const walletQ = query(collection(db, "walletTransactions"), where("agentId", "==", user.uid), orderBy("createdAt", "desc"));

      const sortRecords = (records) =>
        records.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt || 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt || 0;
          return bTime - aTime;
        });

      const unsubTx = onSnapshot(txQ, (snap) => {
        const txs = snap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "transactions" }));
        setTransactions((prev) => {
          const other = prev.filter((p) => p._source !== "transactions");
          return sortRecords([...txs, ...other]);
        });
        setLoadingTransactions(false);
      });

      const unsubCred = onSnapshot(credQ, (snap) => {
        const creds = snap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "credentials" }));
        setTransactions((prev) => {
          const other = prev.filter((p) => p._source !== "credentials");
          return sortRecords([...other, ...creds]);
        });
      });

      const unsubWallet = onSnapshot(walletQ, (snap) => {
        const walletTxs = snap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "walletTransactions" }));
        setTransactions((prev) => {
          const other = prev.filter((p) => p._source !== "walletTransactions");
          return sortRecords([...other, ...walletTxs]);
        });
      });

      return () => {
        unsubTx();
        unsubCred();
        unsubWallet();
      };
    } catch (err) {
      console.error(err);
      setTransactions([]);
      setLoadingTransactions(false);
    }
  }, [user]);

  // Manual refresh for transactions after an order is created
  const refreshTransactions = async () => {
    if (!user) return;
    try {
      const credSnap = await getDocs(query(collection(db, "credentials"), where("agentId", "==", user.uid), orderBy("createdAt", "desc")));
      const creds = credSnap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "credentials" }));
      
      const walletSnap = await getDocs(query(collection(db, "walletTransactions"), where("agentId", "==", user.uid), orderBy("createdAt", "desc")));
      const walletTxs = walletSnap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "walletTransactions" }));

      const txSnap = await getDocs(query(collection(db, "transactions"), where("userId", "==", user.uid), orderBy("createdAt", "desc")));
      const txs = txSnap.docs.map((d) => ({ id: d.id, ...d.data(), _source: "transactions" }));

      const sortRecords = (records) =>
        records.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt || 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt || 0;
          return bTime - aTime;
        });

      setTransactions(sortRecords([...txs, ...creds, ...walletTxs]));
      console.log(`[AgentDashboard] Transactions refreshed: ${creds.length} credentials, ${walletTxs.length} wallet, ${txs.length} transactions`);
    } catch (err) {
      console.error("Error refreshing transactions:", err);
    }
  };


  // realtime manual payment settings loader from admin settings
  useEffect(() => {
    const paymentDoc = doc(db, "settings", "payment");
    try {
      const unsub = onSnapshot(paymentDoc, (snap) => {
        if (!snap.exists()) {
          setManualPaymentNumber(DEFAULT_MANUAL_PAYMENT_NUMBER);
          setManualPaymentName(DEFAULT_MANUAL_PAYMENT_NAME);
          return;
        }

        const paymentData = snap.data();
        setManualPaymentNumber(paymentData.paymentNumber || DEFAULT_MANUAL_PAYMENT_NUMBER);
        setManualPaymentName(paymentData.paymentName || DEFAULT_MANUAL_PAYMENT_NAME);
      }, (err) => console.error(err));
      return () => unsub();
    } catch (e) {
      console.error(e);
    }
  }, []);

  // realtime user balance listener
  useEffect(() => {
    if (!user) return;
    const userDoc = doc(db, "users", user.uid);
    const unsub = onSnapshot(userDoc, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setWalletBalance(Number(d?.walletBalance || 0).toFixed(2));
      }
    }, (err) => console.error(err));
    return () => unsub();
  }, [user]);

  if (selectedTile === "Airtime") {
    return (
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8 bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="flex items-center justify-between rounded-[1.75rem] border-2 border-black dark:border-white bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:bg-slate-950 dark:shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
            <button
              type="button"
              onClick={() => setSelectedTile(null)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-black dark:border-white bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Buy Airtime</p>
          </div>

          <section className="rounded-[2rem] border-2 border-black dark:border-white bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] dark:bg-slate-950 dark:shadow-[0_24px_80px_rgba(15,23,42,0.35)]">
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.3em] text-sky-600 dark:text-sky-400/80">Airtime</p>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Buy airtime quickly</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">Select a network, enter the phone number and amount, then submit your request.</p>
            </div>

            <div className="mt-8 space-y-8">
              {/* Selected Network Display - More Prominent */}
              <div
                className={`rounded-[2rem] border-3 p-8 text-center ${
                  airtimeNetwork === "MTN"
                    ? "border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100/50 dark:from-yellow-950/30 dark:to-yellow-900/20"
                    : airtimeNetwork === "Telecel"
                    ? "border-red-400 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20"
                    : "border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20"
                }`}
              >
                <div
                  className={`mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full font-bold text-2xl ${
                    airtimeNetwork === "MTN"
                      ? "border-4 border-yellow-300 bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300"
                      : airtimeNetwork === "Telecel"
                      ? "border-4 border-red-300 bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                      : "border-4 border-blue-300 bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                  }`}
                >
                  {airtimeNetwork}
                </div>
                <p className="text-xs uppercase tracking-[0.4em] font-bold text-slate-600 dark:text-slate-400 mb-2">Selected Network</p>
                <p className="text-4xl font-black text-slate-900 dark:text-white">{airtimeNetwork}</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <NetworkStatusBadge status={networkStatus?.[airtimeNetwork]?.status} />
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{(networkStatus?.[airtimeNetwork]?.status || 'good').toUpperCase()}</span>
                </div>
              </div>

              {/* Form Section */}
              <div className="space-y-6 rounded-[1.75rem] border-2 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 p-6">
                {/* Network Selection */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-3">Select Network</label>
                  <select
                    value={airtimeNetwork}
                    onChange={(e) => setAirtimeNetwork(e.target.value)}
                    className="w-full rounded-2xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-5 py-4 text-base font-semibold text-slate-900 dark:text-white outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-900"
                  >
                    {NETWORK_OPTIONS.map((network) => (
                      <option key={network} value={network}>{network}</option>
                    ))}
                  </select>
                </div>

                {/* Phone Number Input */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-3">Phone Number</label>
                  <input
                    type="text"
                    value={airtimeNumber}
                    onChange={(e) => setAirtimeNumber(e.target.value)}
                    placeholder="e.g., 0241234567"
                    className="w-full rounded-2xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-5 py-4 text-base font-semibold text-slate-900 dark:text-white outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-900 placeholder:text-slate-500"
                  />
                </div>

                {/* Amount Input */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-3">Amount (GHS)</label>
                  <input
                    type="number"
                    value={airtimeAmount}
                    onChange={(e) => setAirtimeAmount(e.target.value)}
                    placeholder="e.g., 5.00"
                    className="w-full rounded-2xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-5 py-4 text-base font-semibold text-slate-900 dark:text-white outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-900 placeholder:text-slate-500"
                  />
                </div>

                {/* Error Message */}
                {airtimeError && (
                  <div className="rounded-2xl bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800 px-5 py-3">
                    <p className="text-sm font-semibold text-red-700 dark:text-red-300">{airtimeError}</p>
                  </div>
                )}

                {/* Success Message */}
                {airtimeStatus && (
                  <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-200 dark:border-emerald-800 px-5 py-3">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{airtimeStatus}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="button"
                  disabled={loadingAirtime}
                  onClick={handleAirtimePurchase}
                  className="w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-4 text-base font-bold text-white transition hover:shadow-lg hover:from-violet-700 hover:to-fuchsia-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loadingAirtime ? "Sending request..." : "Buy Airtime"}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (selectedTile === "Data Bundle") {
    const dataNetworkShortLabel = dataNetwork === "AirtelTigo" ? "AT" : dataNetwork;

    return (
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8 bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="flex items-center justify-between rounded-[1.75rem] border-2 border-black bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white dark:bg-slate-950 dark:shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
            <button
              type="button"
              onClick={() => setSelectedTile(null)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-black bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:border-white dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Buy Data Bundle</p>
          </div>

          <section className="rounded-[2rem] border-2 border-black bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)] dark:border-white dark:bg-slate-950 dark:shadow-[0_24px_80px_rgba(15,23,42,0.35)]">
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-[0.3em] text-sky-600 dark:text-sky-400/80">Data Bundle</p>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Buy data bundle quickly</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">Select network, choose package, enter phone number.</p>
            </div>

            <div className="mt-8 space-y-8">
              {/* Selected Network Display */}
              <div
                className={`rounded-[2rem] border-2 p-8 text-center ${
                  dataNetwork === "MTN"
                    ? "border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100/50 dark:from-yellow-950/30 dark:to-yellow-900/20"
                    : dataNetwork === "Telecel"
                    ? "border-red-400 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20"
                    : "border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20"
                }`}
              >
                <div
                  className={`mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full text-2xl font-bold ${
                    dataNetwork === "MTN"
                      ? "border-4 border-yellow-300 bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300"
                      : dataNetwork === "Telecel"
                      ? "border-4 border-red-300 bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                      : "border-4 border-blue-300 bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                  }`}
                >
                  {dataNetworkShortLabel}
                </div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.4em] text-slate-600 dark:text-slate-400">Selected Network</p>
                <p className="text-4xl font-black text-slate-900 dark:text-white">{dataNetwork}</p>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <NetworkStatusBadge status={networkStatus?.[dataNetwork]?.status} />
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{(networkStatus?.[dataNetwork]?.status || "good").toUpperCase()}</span>
                </div>
              </div>

              {/* Form Section */}
              <div className="space-y-6 rounded-[1.75rem] border-2 border-slate-200 bg-slate-50/50 p-6 dark:border-slate-700 dark:bg-slate-900/20">
                <div>
                  <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">Select Network</label>
                  <select
                    value={dataNetwork}
                    onChange={(e) => {
                      setDataNetwork(e.target.value);
                      setSelectedDataPackageId("");
                    }}
                    className="w-full rounded-2xl border-2 border-slate-300 bg-white px-5 py-4 text-base font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900"
                  >
                    {NETWORK_OPTIONS.map((network) => (
                      <option key={network} value={network}>{network}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">Choose Package</label>
                  <select
                    value={selectedDataPackageId}
                    onChange={(e) => setSelectedDataPackageId(e.target.value)}
                    disabled={loadingPackages}
                    className="w-full rounded-2xl border-2 border-slate-300 bg-white px-5 py-4 text-base font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900"
                  >
                    <option value="" disabled>{loadingPackages ? "Loading packages..." : "Select a package"}</option>
                    {packagesForNetwork.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>{pkg.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">Beneficiary Phone Number</label>
                  <input
                    value={beneficiaryPhone}
                    onChange={(e) => setBeneficiaryPhone(e.target.value)}
                    placeholder="024xxxxxxx"
                    className="w-full rounded-2xl border-2 border-slate-300 bg-white px-5 py-4 text-base font-semibold text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-200 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:focus:ring-sky-900"
                  />
                </div>

                {dataBundleError && (
                  <div className="rounded-2xl border-2 border-red-200 bg-red-50 px-5 py-3 dark:border-red-800 dark:bg-red-950/30">
                    <p className="text-sm font-semibold text-red-700 dark:text-red-300">{dataBundleError}</p>
                  </div>
                )}

                {dataBundleStatus && (
                  <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 px-5 py-3 dark:border-emerald-800 dark:bg-emerald-950/30">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">{dataBundleStatus}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleDataBundlePurchase}
                  disabled={loadingDataBundle}
                  className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-base font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loadingDataBundle ? "Sending request..." : "Buy Data Bundle"}
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Wallet fullscreen view
  if (showWalletView) {
    return (
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8 bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowWalletView(false)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-black bg-white px-3 py-2 text-sm font-semibold text-slate-700 dark:border-white dark:bg-slate-900 dark:text-slate-100"
            >
              <ChevronLeft size={16} />
              Back
            </button>
          </div>

          <section className="rounded-[1rem] bg-white p-8 text-slate-900 shadow-lg dark:bg-slate-950 dark:text-white">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="rounded-lg border-4 border-dashed border-black bg-white p-6 lg:w-2/3 dark:border-white/50 dark:bg-slate-950">
                <p className="text-sm">Wallet balance</p>
                <h2 className="mt-2 text-3xl font-extrabold">GHS {walletBalance}</h2>
                  <div className="mt-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm">Commission</p>
                    <p className="font-semibold">GHS -</p>
                  </div>
                  <div>
                    <p className="text-sm">Cashback</p>
                    <p className="font-semibold">GHS 0.00</p>
                  </div>
                  <button onClick={() => setShowDepositModal(true)} className="ml-4 rounded-2xl bg-white/10 px-4 py-2 font-semibold text-white">Top Up Wallet</button>
                </div>
              </div>

              <div className="lg:w-1/3">
                <p className="text-sm"> Mobile Money number</p>
                <p className="mt-1 font-semibold">{profileName}</p>
              </div>
            </div>
          </section>

          <section className="mt-8 rounded-lg bg-white p-6 shadow dark:bg-slate-950 dark:shadow-[0_20px_80px_rgba(15,23,42,0.3)]">
            <h3 className="mb-4 text-lg font-semibold text-slate-700">Transactions</h3>
            <div className="overflow-auto">
              <table className="w-full table-auto text-left">
                <thead>
                  <tr className="text-sm text-slate-500 dark:text-slate-300">
                    <th className="py-2">ID</th>
                    <th className="py-2">Reference</th>
                    <th className="py-2">Type</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2">Balance Before</th>
                    <th className="py-2">Balance After</th>
                    <th className="py-2">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTransactions ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-sm text-slate-500">Loading transactions...</td>
                    </tr>
                  ) : transactions && transactions.length ? (
                    transactions.map((t) => (
                      <tr key={t.id} className="border-t-2">
                        <td className="py-3 text-sm">{t.id}</td>
                        <td className="py-3 text-sm">{t.reference || t.requestNumber || t.id}</td>
                        <td className="py-3 text-sm">
                          {t.type
                            ? t.type
                            : t._source === "credentials"
                            ? t.packageId || t.status || "Purchase"
                            : "-"}
                        </td>
                        <td className="py-3 text-sm">
                          {t.amount || t.totalPaid || t.price
                            ? `GHS ${Number(t.amount ?? t.totalPaid ?? t.price).toFixed(2)}`
                            : "-"}
                        </td>
                        <td className="py-3 text-sm">{t.balanceBefore ? `GHS ${Number(t.balanceBefore).toFixed(2)}` : "-"}</td>
                        <td className="py-3 text-sm">{t.balanceAfter ? `GHS ${Number(t.balanceAfter).toFixed(2)}` : "-"}</td>
                        <td className="py-3 text-sm">{t.commission ? `GHS ${Number(t.commission).toFixed(2)}` : "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-sm text-slate-500">No transactions yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (selectedTile === "Purchases") {
    const sortedResults = [...purchaseSearchResults].sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt).getTime();
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt).getTime();
      return bTime - aTime;
    });
    const currentPurchase = sortedResults[0];
    const pastPurchases = sortedResults.slice(1);

    const getPurchaseDate = (txn) => {
      if (txn?.date) return txn.date;
      if (!txn?.createdAt) return "N/A";
      return formatPurchaseDate(txn.createdAt);
    };

    const formatPurchaseDate = (value) => {
      if (!value) return "N/A";
      const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
      return date.toLocaleString();
    };

    return (
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8 bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="mx-auto max-w-3xl space-y-5">
          <div className="flex items-center justify-between rounded-3xl border-2 border-black bg-white p-4 shadow-lg dark:border-white dark:bg-slate-950 dark:shadow-[0_20px_40px_rgba(15,23,42,0.35)]">
            <button
              type="button"
              onClick={() => setSelectedTile(null)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-black bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:border-white dark:bg-slate-900/90 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">Purchase History</p>
          </div>

          <div className="mt-4 space-y-3 rounded-3xl border-2 border-black bg-white p-6 shadow-lg dark:border-white dark:bg-slate-950/90 dark:shadow-[0_20px_80px_rgba(15,23,42,0.35)]">
            <p className="text-base font-bold text-slate-900 dark:text-slate-100">Purchase History</p>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
              <motion.input
                value={purchaseSearchPhone}
                onChange={(e) => setPurchaseSearchPhone(e.target.value)}
                maxLength={10}
                placeholder="Enter phone used for purchase"
                className="w-full rounded-2xl border-2 border-black bg-white/80 px-12 pr-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 dark:border-white dark:bg-slate-950 dark:text-slate-100"
                whileFocus={{ scale: 1.03, boxShadow: "0 0 0 2px rgba(39, 65, 147, 0.2)" }}
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              />
            </div>
            <button
              type="button"
              onClick={handleLoadPurchases}
              disabled={loadingPurchaseSearch}
              className="w-full rounded-2xl bg-[#274193] py-3 font-bold text-white"
            >
              {loadingPurchaseSearch ? "Loading..." : "Load Purchases"}
            </button>

            {purchaseSearchError && (
              <p className="rounded-3xl bg-red-100 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                {purchaseSearchError}
              </p>
            )}

            <div className="space-y-2">
              {currentPurchase && (
                <>
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Current Purchase</p>
                  <div className="rounded-xl border-4 border-[#274193] bg-blue-50 p-4 text-sm dark:bg-blue-950/20">
                    <p className="font-bold text-slate-900 dark:text-slate-100">{currentPurchase.packageId || currentPurchase.product || currentPurchase.type || "Data Bundle"}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#274193] dark:text-sky-300">{(currentPurchase.type || "data").toUpperCase()}</p>
                    <p className="text-slate-600 dark:text-slate-300">{currentPurchase.assignedTo || currentPurchase.requestNumber || currentPurchase.beneficiary || "N/A"}</p>
                    <p className="text-slate-500 text-xs dark:text-slate-500">{getPurchaseDate(currentPurchase)}</p>
                    <div className="mt-3">
                      <StatusBadge status={currentPurchase.status} />
                    </div>
                  </div>
                </>
              )}

              {pastPurchases.length > 0 && (
                <>
                  <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Past Purchases</p>
                  <div className="space-y-2">
                    {pastPurchases.map((txn) => (
                      <div key={txn.id} className="rounded-xl border-2 border-black bg-white p-3 text-sm dark:border-white dark:bg-slate-950/80">
                        <p className="font-bold text-slate-700 dark:text-slate-100">{txn.packageId || txn.product || txn.type || "Data Bundle"}</p>
                        <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#274193] dark:text-sky-300">{(txn.type || "data").toUpperCase()}</p>
                        <p className="text-slate-500 dark:text-slate-300">{txn.assignedTo || txn.requestNumber || txn.beneficiary || "N/A"}</p>
                        <p className="text-slate-400 text-xs dark:text-slate-500">{getPurchaseDate(txn)}</p>
                        <div className="mt-2">
                          <StatusBadge status={txn.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {!currentPurchase && !pastPurchases.length && (
                <p className="text-sm text-slate-500 dark:text-slate-400">No purchases loaded yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pageWrapperClass = isDark ? "min-h-screen bg-black text-slate-100" : "min-h-screen bg-white text-slate-900";
  const topSectionClass = isDark
    ? "rounded-[2rem] border border-white/10 bg-black p-8 shadow-[0_30px_80px_rgba(15,23,42,0.35)]"
    : "rounded-[2rem] border border-slate-200 bg-white p-8 shadow-lg";

  return (
    <div className={`${pageWrapperClass} px-4 py-8 sm:px-6 lg:px-8`}>
      <div className="mx-auto max-w-6xl space-y-8">
        <section className={topSectionClass}>
          <div className="relative">
            <div className="flex items-start justify-between gap-6">
              <div className="max-w-2xl">
                <p className="text-sm uppercase tracking-[0.3em] text-sky-400/80">Agent dashboard</p>
                <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white sm:text-3xl">
                  <span className="block">Hello,</span>
                  <span className="block text-2xl font-semibold text-slate-700 dark:text-slate-200 sm:text-[1.5rem]">{profileName}</span>
                </h1>
              </div>

              <div className="relative flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTheme(isDark ? "light" : "dark")}
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-full border-2 px-3 py-3 transition ${
                    isDark
                      ? "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  aria-label="Toggle theme"
                >
                  {isDark ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <button
                  type="button"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-black bg-white px-3 py-3 text-slate-700 transition hover:bg-slate-50 dark:border-white dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <Menu size={20} />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full z-20 mt-3 w-52 overflow-hidden rounded-3xl border-2 border-black bg-white p-3 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white dark:bg-slate-950/95 dark:shadow-[0_20px_60px_rgba(15,23,42,0.5)]">
                    {menuItems.map((item) => {
                      const MenuIcon = item.icon;
                      return (
                        <button
                          key={item.title}
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            if (item.title === "Wallet") {
                              setShowWalletView(true);
                              setSelectedTile(null);
                            } else {
                              setSelectedTile(item.value);
                            }
                          }}
                          className="flex w-full items-center gap-3 rounded-3xl px-3 py-3 text-start text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
                        >
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-200">
                            <MenuIcon size={16} />
                          </span>
                          {item.title}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        handleLogout();
                      }}
                      className="mt-2 flex w-full items-center gap-3 rounded-3xl bg-red-600 px-4 py-3 text-start text-sm font-bold text-white shadow-lg transition-all hover:bg-red-700 hover:scale-105 active:scale-95"
                    >
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-red-700 text-white">
                        <LogOut size={18} />
                      </span>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Dashboard</h2>
              <button
                type="button"
                onClick={refreshTransactions}
                disabled={loadingTransactions}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                <RefreshCcw size={16} className={loadingTransactions ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
              <div className="col-span-full rounded-3xl border border-slate-200 bg-white p-5 text-center shadow-lg dark:border-white/10 dark:bg-slate-950/80 dark:shadow-[0_20px_40px_rgba(15,23,42,0.15)]">
                <p className="text-sm uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">Wallet balance</p>
                <p className="mt-3 text-3xl font-black text-slate-900 dark:text-white">GHS {walletBalance}</p>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{walletGrowth}</p>
                {activeSales && (
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{activeSales}</p>
                )}
                <button
                  type="button"
                  onClick={() => setShowDepositModal(true)}
                  className="mt-5 inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  Top Up Wallet
                </button>
              </div>
            </div>
          </div>
        </section>
        {showDepositModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border p-6 shadow-2xl ${
                isDark
                  ? "border-slate-700/50 bg-gradient-to-br from-slate-800 to-slate-900"
                  : "border-white/50 bg-gradient-to-br from-white to-slate-50/50"
              } backdrop-blur-sm`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-blue-500/5 rounded-3xl" />

              <div className="relative space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Top Up Wallet</h2>
                  <button
                    onClick={() => setShowDepositModal(false)}
                    className={`rounded-full p-2 transition-colors ${
                      isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
                    }`}
                  >
                    <X size={20} />
                  </button>
                </div>

                {errorMsg && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-2xl p-4 text-sm font-semibold ${
                      isDark ? "bg-red-900/20 text-red-300" : "bg-red-100 text-red-700"
                    }`}
                  >
                    {errorMsg}
                  </motion.div>
                )}

                {successMsg && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-2xl p-4 text-sm font-semibold ${
                      isDark ? "bg-green-900/20 text-green-300" : "bg-green-100 text-green-700"
                    }`}
                  >
                    {successMsg}
                  </motion.div>
                )}

                {/* Amount Input */}
                <div className="space-y-3">
                  <label className={`block text-sm font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    Top-up Amount (GHS)
                  </label>
                  <div
                    className={`relative rounded-2xl border-2 transition-all ${
                      isDark
                        ? "border-slate-600 bg-slate-800/50 focus-within:border-green-500"
                        : "border-slate-300 bg-white focus-within:border-green-500"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="flex items-center justify-center px-4 py-4">
                        <span className={`text-lg font-bold ${isDark ? "text-slate-300" : "text-slate-500"}`}>
                          GH₵
                        </span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={topupAmount === 0 ? "" : topupAmount}
                        onChange={(e) => setTopupAmount(Math.max(0, Number(e.target.value) || 0))}
                        placeholder="0.00"
                        className={`flex-1 bg-transparent px-2 py-4 text-lg font-bold outline-none ${
                          isDark ? "text-slate-100 placeholder:text-slate-400" : "text-slate-900 placeholder:text-slate-500"
                        }`}
                      />
                      <div className="flex flex-col border-l border-slate-200 dark:border-slate-600">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setTopupAmount((prev) => Math.max(0, prev + 10))}
                          className={`px-3 py-1 transition-colors ${
                            isDark ? "text-slate-400 hover:text-green-400" : "text-slate-500 hover:text-green-600"
                          } hover:bg-green-50 dark:hover:bg-green-900/20`}
                        >
                          <ChevronDown className="h-4 w-4 rotate-180" />
                        </motion.button>
                        <div className="h-px bg-slate-200 dark:bg-slate-600" />
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setTopupAmount((prev) => Math.max(0, prev - 10))}
                          className={`px-3 py-1 transition-colors ${
                            isDark ? "text-slate-400 hover:text-red-400" : "text-slate-500 hover:text-red-600"
                          } hover:bg-red-50 dark:hover:bg-red-900/20`}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="flex gap-2">
                    {[50, 100, 200, 500].map((amount) => (
                      <motion.button
                        key={amount}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setTopupAmount(amount)}
                        className={`rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                          topupAmount === amount
                            ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg"
                            : isDark
                              ? "bg-slate-700/50 text-slate-300 hover:bg-slate-600/50"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        GH₵ {amount}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <label className={`block text-sm font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    Payment Method
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setTopupMethod("paystack")}
                      className={`relative overflow-hidden rounded-2xl border-2 px-4 py-4 text-sm font-bold transition-all ${
                        topupMethod === "paystack"
                          ? "border-blue-500 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 shadow-lg dark:from-blue-900/20 dark:to-blue-800/20 dark:text-blue-300"
                          : isDark
                            ? "border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500"
                            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <CreditCard size={20} />
                        <span>Paystack</span>
                        <span className="text-xs opacity-75">Instant</span>
                      </div>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setTopupMethod("manual")}
                      className={`relative overflow-hidden rounded-2xl border-2 px-4 py-4 text-sm font-bold transition-all ${
                        topupMethod === "manual"
                          ? "border-amber-500 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 shadow-lg dark:from-amber-900/20 dark:to-amber-800/20 dark:text-amber-300"
                          : isDark
                            ? "border-slate-600 bg-slate-800/50 text-slate-300 hover:border-slate-500"
                            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <UserCheck size={20} />
                        <span>Manual</span>
                        <span className="text-xs opacity-75">Admin Approved</span>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Manual Payment Details */}
                {topupMethod === "manual" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className={`rounded-2xl border p-4 space-y-4 ${
                      isDark
                        ? "border-amber-500/30 bg-gradient-to-r from-amber-900/10 to-amber-800/10"
                        : "border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-amber-600" />
                      <span className={`text-sm font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                        Direct Deposit Details
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          MOMO Number:
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                            {manualPaymentNumber}
                          </span>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              navigator.clipboard.writeText(manualPaymentNumber);
                              setCopiedField("number");
                              setTimeout(() => setCopiedField(null), 2000);
                            }}
                            className={`rounded-lg p-2 transition-colors ${
                              isDark
                                ? "bg-amber-900/20 text-amber-300 hover:bg-amber-800/30"
                                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            }`}
                          >
                            {copiedField === "number" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                          </motion.button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          Account Name:
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                            {manualPaymentName}
                          </span>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              navigator.clipboard.writeText(manualPaymentName);
                              setCopiedField("name");
                              setTimeout(() => setCopiedField(null), 2000);
                            }}
                            className={`rounded-lg p-2 transition-colors ${
                              isDark
                                ? "bg-amber-900/20 text-amber-300 hover:bg-amber-800/30"
                                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            }`}
                          >
                            {copiedField === "name" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                          </motion.button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <span className={`text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          Payment Reference:
                        </span>
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex-1 rounded-lg px-3 py-2 font-mono text-sm font-bold ${
                              isDark ? "bg-slate-800 text-slate-100" : "bg-slate-100 text-slate-900"
                            }`}
                          >
                            {manualReference || (
                              <span className={`italic ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                                Click Generate to create reference
                              </span>
                            )}
                          </div>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={generateManualReference}
                            className="rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-bold text-white shadow-lg hover:from-blue-600 hover:to-blue-700"
                          >
                            Generate
                          </motion.button>
                          {manualReference && (
                            <motion.button
                              type="button"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                navigator.clipboard.writeText(manualReference);
                                setCopiedField("ref");
                                setTimeout(() => setCopiedField(null), 2000);
                              }}
                              className={`rounded-lg p-2 transition-colors ${
                                isDark
                                  ? "bg-slate-700 text-slate-300 hover:bg-slate-600"
                                  : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                              }`}
                            >
                              {copiedField === "ref" ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                            </motion.button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className={`block text-sm font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                          Reason (Optional)
                        </label>
                        <textarea
                          value={topupManualReason}
                          onChange={(e) => setTopupManualReason(e.target.value)}
                          placeholder="Provide context for your top-up request..."
                          rows={3}
                          className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all ${
                            isDark
                              ? "border-slate-600 bg-slate-800/50 text-slate-100 placeholder:text-slate-400 focus:border-blue-500"
                              : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
                          }`}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Top Up Action Button */}
                <motion.button
                  whileHover={{
                    scale: topupAmount >= 20 && !loadingTopup ? 1.02 : 1,
                  }}
                  whileTap={{ scale: topupAmount >= 20 && !loadingTopup ? 0.98 : 1 }}
                  onClick={
                    topupMethod === "paystack"
                      ? handleTopupWithPaystack
                      : handleManualPaymentConfirmation
                  }
                  disabled={
                    loadingTopup ||
                    topupAmount < 20 ||
                    (topupMethod === "manual" && !String(manualReference || "").trim())
                  }
                  className={`relative w-full overflow-hidden rounded-2xl px-6 py-4 text-lg font-bold transition-all ${
                    !loadingTopup &&
                    topupAmount >= 20 &&
                    (topupMethod === "paystack" ||
                      String(manualReference || "").trim())
                      ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-xl hover:from-green-600 hover:to-green-700"
                      : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                  }`}
                >
                  {loadingTopup ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Processing...
                    </div>
                  ) : topupMethod === "paystack" ? (
                    <div className="flex items-center justify-center gap-2">
                      <CreditCard size={20} />
                      Top Up Wallet
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Send size={20} />
                      I Have Paid
                    </div>
                  )}
                </motion.button>

                {topupMethod === "manual" && (
                  <p
                    className={`text-center text-xs leading-relaxed ${
                      isDark ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    Generate a reference first, then tap "I Have Paid" to alert admin.
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        )}


        <section className="grid grid-cols-2 gap-3 xl:grid-cols-3">
          {actionTiles.map((tile) => {
            const TileIcon = tile.icon;
            const active = selectedTile === tile.title;
            return (
              <article
                key={tile.title}
                onClick={() => {
                  if (tile.title === "Wallet") {
                    setShowWalletView(true);
                  } else {
                    setSelectedTile(tile.title);
                  }
                }}
                className={`group cursor-pointer overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 text-center transition dark:border-slate-700 dark:bg-slate-950/90 ${
                  active ? "border-sky-400/80 bg-sky-500/10 dark:bg-slate-900/90" : "hover:border-slate-300 hover:bg-slate-50 dark:hover:border-slate-600 dark:hover:bg-slate-900"
                }`}
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-3xl bg-slate-100 text-slate-900 transition group-hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:group-hover:bg-slate-800">
                  <TileIcon size={20} />
                </div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{tile.title}</p>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}


